import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import BtnTaratic from './BtnTaratic';
import AppModal from './AppModal';

export type DeleteConfirmationOptions = {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
};

type PendingConfirmation = DeleteConfirmationOptions & {
  resolve: (confirmed: boolean) => void;
};

type DeleteConfirmationContextValue = {
  confirmDelete: (options: DeleteConfirmationOptions) => Promise<boolean>;
};

const DeleteConfirmationContext = createContext<DeleteConfirmationContextValue | null>(null);

export const DeleteConfirmationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [pending, setPending] = useState<PendingConfirmation | null>(null);

  const confirmDelete = useCallback((options: DeleteConfirmationOptions) => new Promise<boolean>((resolve) => {
    setPending({ ...options, resolve });
  }), []);

  const close = useCallback((confirmed: boolean) => {
    setPending((current) => {
      current?.resolve(confirmed);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!pending) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [close, pending]);

  return (
    <DeleteConfirmationContext.Provider value={{ confirmDelete }}>
      {children}
      {pending && (
        <AppModal open onClose={() => close(false)} title={pending.title || 'Confirmar eliminaci?n'}
          footer={<>
            <BtnTaratic color="ghost-dark" type="button" onClick={() => close(false)} autoFocus>{pending.cancelLabel || 'Cancelar'}</BtnTaratic>
            <BtnTaratic type="button" color="red" onClick={() => close(true)}>{pending.confirmLabel || 'Eliminar'}</BtnTaratic>
          </>}>
          <div data-ui="delete-confirmation-message" className="text-sm leading-6">{pending.message}</div>
        </AppModal>
      )}
    </DeleteConfirmationContext.Provider>
  );
};

export const useDeleteConfirmation = (): DeleteConfirmationContextValue['confirmDelete'] => {
  const context = useContext(DeleteConfirmationContext);
  if (!context) {
    throw new Error('useDeleteConfirmation must be used within DeleteConfirmationProvider');
  }
  return context.confirmDelete;
};
